import { useState, useMemo } from 'react'
import { useGetFleetProductivity } from '../../../api/routes/fleet'
import { MetricCard } from './components/metric-card'
import { PeriodSelector } from './components/period-selector'
import { ComparisonCard } from './components/comparison-card'
import { Clock, Ruler, TrendingUp, BarChart3, Gauge } from 'lucide-react'
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Legend,
} from 'recharts'
import {
    useReactTable,
    getCoreRowModel,
    getSortedRowModel,
    flexRender,
    type SortingState,
    type ColumnDef,
} from '@tanstack/react-table'

const periodOptions = [
    { value: '7d', label: 'Last 7 days' },
    { value: '30d', label: 'Last 30 days' },
    { value: 'thisMonth', label: 'This month' },
]

const ProductivitySkeleton = () => (
    <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[...Array(5)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="h-24 bg-muted rounded-lg" />
            <div className="h-24 bg-muted rounded-lg" />
        </div>
        <div className="h-64 bg-muted rounded-lg" />
        <div className="h-64 bg-muted rounded-lg" />
    </div>
)

type DeviceRow = {
    deviceId: string
    deviceName: string
    businessName: string | null
    machineRunHours: number
    totalAreaGround: number
}

const columns: ColumnDef<DeviceRow>[] = [
    {
        accessorKey: 'deviceName',
        header: 'Device',
        cell: (info) => (
            <span className="font-medium">{info.getValue() as string}</span>
        ),
    },
    {
        accessorKey: 'businessName',
        header: 'Business',
        cell: (info) => (
            <span className="text-muted-foreground">
                {(info.getValue() as string) || '—'}
            </span>
        ),
    },
    {
        accessorKey: 'machineRunHours',
        header: 'Hours',
        cell: (info) =>
            (info.getValue() as number).toLocaleString(undefined, {
                maximumFractionDigits: 1,
            }),
    },
    {
        accessorKey: 'totalAreaGround',
        header: 'Area (m²)',
        cell: (info) =>
            (info.getValue() as number).toLocaleString(undefined, {
                maximumFractionDigits: 0,
            }),
    },
]

export const ProductivityTab = () => {
    const [period, setPeriod] = useState('30d')
    const { data, isLoading } = useGetFleetProductivity(period)
    const [sorting, setSorting] = useState<SortingState>([
        { id: 'totalAreaGround', desc: true },
        { id: 'machineRunHours', desc: true },
    ])

    const table = useReactTable({
        data: data?.devices || [],
        columns,
        state: { sorting },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    })

    if (isLoading) return <ProductivitySkeleton />
    if (!data) return null

    const { summary, comparison, dailyTrend } = data

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex justify-end">
                <PeriodSelector
                    value={period}
                    onChange={setPeriod}
                    options={periodOptions}
                />
            </div>

            {/* Summary Metrics */}
            <div>
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Fleet Summary
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                    <MetricCard
                        title="Total Hours"
                        value={summary.totalMachineHours.toLocaleString(
                            undefined,
                            { maximumFractionDigits: 1 }
                        )}
                        description={`${summary.deviceCount} devices`}
                        color="bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-400"
                    />
                    <MetricCard
                        title="Total Area"
                        value={`${summary.totalAreaGround.toLocaleString(undefined, { maximumFractionDigits: 0 })} m²`}
                        color="bg-green-50 text-green-600 dark:bg-green-950 dark:text-green-400"
                    />
                    <MetricCard
                        title="Avg Daily Hours"
                        value={summary.avgDailyHours.toLocaleString(undefined, {
                            maximumFractionDigits: 1,
                        })}
                        color="bg-purple-50 text-purple-600 dark:bg-purple-950 dark:text-purple-400"
                    />
                    <MetricCard
                        title="Avg Daily m²"
                        value={summary.avgDailySqm.toLocaleString(undefined, {
                            maximumFractionDigits: 0,
                        })}
                        color="bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"
                    />
                    <MetricCard
                        title="Avg m²/Hr"
                        value={summary.avgSqmPerHour.toLocaleString(undefined, {
                            maximumFractionDigits: 1,
                        })}
                        color="bg-cyan-50 text-cyan-600 dark:bg-cyan-950 dark:text-cyan-400"
                    />
                </div>
            </div>

            {/* Monthly Comparison */}
            {comparison && (
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Monthly Comparison
                    </h2>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <ComparisonCard
                            title="Machine Hours"
                            currentValue={
                                comparison.currentMonth.machineRunHours
                            }
                            lastValue={comparison.lastMonth.machineRunHours}
                            changePercent={comparison.machineHoursChange}
                            unit="hrs"
                            monthProgress={comparison.monthProgress}
                        />
                        <ComparisonCard
                            title="Area Ground"
                            currentValue={
                                comparison.currentMonth.totalAreaGround
                            }
                            lastValue={comparison.lastMonth.totalAreaGround}
                            changePercent={comparison.areaGroundChange}
                            unit="m²"
                            monthProgress={comparison.monthProgress}
                        />
                    </div>
                </div>
            )}

            {/* Daily Trend Chart */}
            {dailyTrend.length > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Daily Trend
                    </h2>
                    <div className="rounded-lg border bg-card p-4 sm:p-5">
                        <ResponsiveContainer width="100%" height={300}>
                            <AreaChart data={dailyTrend}>
                                <CartesianGrid
                                    strokeDasharray="3 3"
                                    className="stroke-border"
                                />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(val) => {
                                        const d = new Date(val)
                                        return `${d.getMonth() + 1}/${d.getDate()}`
                                    }}
                                    className="text-muted-foreground"
                                />
                                <YAxis
                                    yAxisId="hours"
                                    tick={{ fontSize: 11 }}
                                    className="text-muted-foreground"
                                />
                                <YAxis
                                    yAxisId="area"
                                    orientation="right"
                                    tick={{ fontSize: 11 }}
                                    className="text-muted-foreground"
                                />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: 'hsl(var(--card))',
                                        border: '1px solid hsl(var(--border))',
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                    }}
                                />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                                <Area
                                    yAxisId="hours"
                                    type="monotone"
                                    dataKey="totalHours"
                                    name="Hours"
                                    stroke="hsl(217, 91%, 60%)"
                                    fill="hsl(217, 91%, 60%)"
                                    fillOpacity={0.1}
                                    strokeWidth={2}
                                />
                                <Area
                                    yAxisId="area"
                                    type="monotone"
                                    dataKey="totalArea"
                                    name="Area (m²)"
                                    stroke="hsl(142, 71%, 45%)"
                                    fill="hsl(142, 71%, 45%)"
                                    fillOpacity={0.1}
                                    strokeWidth={2}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Per-device table */}
            {data.devices.length > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Per Device
                    </h2>
                    <div className="rounded-lg border bg-card overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    {table
                                        .getHeaderGroups()
                                        .map((headerGroup) => (
                                            <tr
                                                key={headerGroup.id}
                                                className="border-b bg-muted/50"
                                            >
                                                {headerGroup.headers.map(
                                                    (header) => (
                                                        <th
                                                            key={header.id}
                                                            className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wide cursor-pointer select-none hover:text-foreground"
                                                            onClick={header.column.getToggleSortingHandler()}
                                                        >
                                                            <div className="flex items-center gap-1">
                                                                {flexRender(
                                                                    header
                                                                        .column
                                                                        .columnDef
                                                                        .header,
                                                                    header.getContext()
                                                                )}
                                                                {{
                                                                    asc: ' ↑',
                                                                    desc: ' ↓',
                                                                }[
                                                                    header.column.getIsSorted() as string
                                                                ] ?? null}
                                                            </div>
                                                        </th>
                                                    )
                                                )}
                                            </tr>
                                        ))}
                                </thead>
                                <tbody>
                                    {table.getRowModel().rows.map((row) => (
                                        <tr
                                            key={row.id}
                                            className="border-b last:border-0 hover:bg-muted/30"
                                        >
                                            {row
                                                .getVisibleCells()
                                                .map((cell) => (
                                                    <td
                                                        key={cell.id}
                                                        className="px-4 py-3 tabular-nums"
                                                    >
                                                        {flexRender(
                                                            cell.column
                                                                .columnDef.cell,
                                                            cell.getContext()
                                                        )}
                                                    </td>
                                                ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
